import { createReducer, on } from '@ngrx/store';
import { authApiActions } from '@core/auth/auth.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { defaultEntityState, IEntityState } from '@core/state/entities/entity';
import {
  removeEntities,
  removeEntitiesByFilter,
} from '@core/state/entities/entity.helpers';
import { entityKey } from '@core/state/entities/entity-name-map';
import { ISearchImportJob } from '@core/state/entities/search-import-job/search-import-job.entity';
import {
  fenceDeletedProject,
  mergeMonotonicProjectJobs,
  ProjectJobFamilyState,
  readIncomingProjectJobs,
  reconcileProjectJobSnapshot,
  resetProjectJobSnapshotVersions,
  searchImportJobFamily,
} from '@core/state/entities/project-job-snapshot';

type SearchImportJobState = ProjectJobFamilyState<ISearchImportJob>;

/**
 * Search import jobs are server-owned and read-only on the client, so this reducer only merges
 * what project payloads carry. Whole-Project snapshots are reconciled against the Project audit
 * version (an older snapshot may never remove a job a newer one created), while single-job
 * payloads are merged by the server-owned per-job revision.
 */
export const searchImportJobReducer = createReducer(
  defaultEntityState<ISearchImportJob>(),
  on(
    projectDetailActions.searchImportJobStarted,
    (state, props): IEntityState<ISearchImportJob> =>
      mergeMonotonicProjectJobs(
        state as SearchImportJobState,
        readIncomingProjectJobs<ISearchImportJob>(searchImportJobFamily, props)
      )
  ),
  on(
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.projectForInvitationRetrieved,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    projectDetailActions.projectUpdated,
    (state, props): IEntityState<ISearchImportJob> =>
      reconcileProjectJobSnapshot(
        searchImportJobFamily,
        state as SearchImportJobState,
        props
      )
  ),
  on(
    projectDetailActions.registered,
    (state, props): IEntityState<ISearchImportJob> =>
      reconcileProjectJobSnapshot(
        searchImportJobFamily,
        state as SearchImportJobState,
        props
      )
  ),
  on(
    authApiActions.startUserImpersonation,
    authApiActions.stopUserImpersonation,
    (state): IEntityState<ISearchImportJob> =>
      resetProjectJobSnapshotVersions(state as SearchImportJobState)
  ),
  on(
    projectDetailActions.projectDeleted,
    (state, props): IEntityState<ISearchImportJob> => {
      const searchState = state as SearchImportJobState;
      return fenceDeletedProject(
        searchState,
        removeEntitiesByFilter(
          searchState,
          (projChild) => projChild.projectId === props.projectId
        ) as SearchImportJobState,
        props.projectId,
        props.terminalPhysicalDeletion
      );
    }
  ),
  on(
    projectDetailActions.searchImportJobDeleted,
    projectDetailActions.searchImportJobCancelled,
    (state, props): IEntityState<ISearchImportJob> =>
      removeEntities(entityKey.searchImportJobs, state, props)
  )
);
